import { inject, Injectable } from '@angular/core';
import { SkyApiService } from '@skyeng/libs/shared/api';
import { SkyResourceService } from '@skyeng/libs/shared/resource';
import { VimCoreAuthService } from '@skyeng/libs/vimbox-core/user';
import { type Observable, Subject, type Subscription } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { type ErrorReportUploadProvider } from './interfaces';
import { ERROR_REPORT_PROXY_ENDPOINT } from './tokens';

interface UploadIntentionResponse {
  url: string;
  fields: {
    key: string;
    [name: string]: string;
  };
}

@Injectable({ providedIn: 'root' })
export class ProxyErrorReportUploadService implements ErrorReportUploadProvider {
  private apiService = inject(SkyApiService);
  private resourceService = inject(SkyResourceService);
  private authService = inject(VimCoreAuthService);
  private proxyEndpoint = inject(ERROR_REPORT_PROXY_ENDPOINT);
  private uploading$ = new Subject<unknown>();

  uploadFile(file: File | Blob, _uploadInitiator?: string): Observable<string> {
    const name = file instanceof File ? file.name : `screenshot-${Date.now()}.png`;

    return this.apiService
      .post<UploadIntentionResponse>(this.proxyEndpoint, '/api/v1/error-report/upload-intension', {
        params: { name, type: file.type, size: file.size },
        token: this.authService.getToken(),
        withCredentials: true,
      })
      .pipe(
        switchMap(response => {
          const uploadFields = Object.fromEntries(
            Object.entries(response.fields).filter(([name]) => name !== 'success_action_status'),
          );

          return this.resourceService
            .uploadFileToS3(response.url, file, uploadFields)
            .pipe(map(() => `${response.url.replace(/\/$/, '')}/${response.fields.key}`));
        }),
      );
  }

  onUploading(callback: (event: unknown) => unknown): Subscription {
    return this.uploading$.subscribe(callback);
  }
}
